import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Skeleton,
  SvgIcon,
} from '@mui/material';
import { ReactComponent as OnTime } from 'assets/images/ic_policy_on_time.svg';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { Utils } from 'shared/helpers';

interface RemediationDeadline {
  _id: string;
  title: string;
  risk_rating: string;
  status: string;
  owner_assignee?: string;
  remaining_days: number;
  target_remediation_date: string;
  createdAt?: string;
}

interface UpcomingRemediationProps {
  data: RemediationDeadline[];
  loading?: boolean;
}

const UpcomingRemediationDeadlines: React.FC<UpcomingRemediationProps> = ({
  data,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card variant="outlined" className="remediation-vulnerabilities">
        <CardHeader title="Upcoming Remediation Deadlines" />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="100%" height={24} />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ width: 120 }}>
                  <Skeleton variant="rectangular" width={120} height={40} />
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card variant="outlined" className="remediation-vulnerabilities">
        <CardHeader title="Upcoming Remediation Deadlines" />
        <CardContent>
          <EmptyPlaceholder
            icon={
              <SvgIcon
                component={OnTime}
                inheritViewBox
                className="view-icon"
                fontSize="inherit"
                htmlColor="#76A81F"
              />
            }
            title="No Vulnerability data available"
            message="All set for now! No Vulnerabilities approaching their target dates"
          />
        </CardContent>
      </Card>
    );
  }

  const sortedData = [...data].sort(
    (a, b) => a.remaining_days - b.remaining_days
  );

  return (
    <Card variant="outlined" className="remediation-vulnerabilities">
      <CardHeader title="Upcoming Remediation Deadlines" />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortedData.map((deadline) => {
            const isOverdue = deadline.remaining_days <= 0;
            const riskColor =
              Utils.RISK_COLOR_MAP[deadline.risk_rating] || '#C8C8C4';
            const statusColor =
              Utils.STATUS_COLOR_MAP[deadline.status] || '#C8C8C4';
            const riskBgColor =
              Utils.RISK_BG_MAP[deadline.risk_rating] || '#C8C8C420';

            return (
              <Box
                key={deadline._id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    gutterBottom
                    noWrap
                  >
                    {deadline.title}
                  </Typography>

                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}
                  >
                    <Chip
                      label={deadline.risk_rating}
                      size="small"
                      sx={{
                        bgcolor: riskBgColor,
                        color: riskColor,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />

                    <Chip
                      label={deadline.status}
                      size="small"
                      sx={{
                        bgcolor: `${statusColor}20`,
                        color: statusColor,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      ID: {deadline._id.slice(-8)}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{ textAlign: 'right', minWidth: { xs: 'auto', sm: 160 } }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isOverdue ? (
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: '#E24B4A' }}
                      >
                        ⚠️ Overdue
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: '#F07A27' }}
                      >
                        {deadline.remaining_days === 1
                          ? `${deadline.remaining_days} day left`
                          : `${deadline.remaining_days} days left`}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Due{' '}
                    {new Date(
                      deadline.target_remediation_date
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default UpcomingRemediationDeadlines;
